/**
 * Delivery Playbooks V2.3 · CRM (/admin/delivery-playbooks)
 *
 * Internal SSOT for how BrightLabs delivers each of the 6 core service
 * lines. Feb 2026 refresh:
 *   · Dark theme to match Pricing Configurator visual language.
 *   · UK / India price toggle — same content, market-aware numbers.
 *   · New "Ongoing Care Plans" block per service so hosting/retainer
 *     options sit next to their delivery playbook.
 */
import { useMemo, useState } from 'react';
import {
  Copy, CheckCircle2, AlertTriangle, MessageCircle, ClipboardList,
  ListChecks, HelpCircle, Sparkles, Info, Server,
} from 'lucide-react';
import { toast } from 'sonner';
import { DELIVERY_PLAYBOOKS, DELIVERY_PLAYBOOK_ORDER, PLAYBOOK_VERSION } from '../../data/deliveryPlaybooks';
import { SELF_AUDIT } from '../../data/playbookGlossary';
import { HighlightJargon } from '../../components/HighlightJargon';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';

const SECTIONS = [
  { key: 'pov',           label: 'Our POV',              icon: Sparkles },
  { key: 'discovery',     label: 'Discovery questions',  icon: HelpCircle },
  { key: 'redFlags',      label: 'Red flags',            icon: AlertTriangle },
  { key: 'deliverables',  label: 'Deliverables',         icon: ClipboardList },
  { key: 'brandingTiers', label: 'Branding & Identity', icon: Sparkles },
  { key: 'careAddOns',    label: 'Care plans',           icon: Server },
  { key: 'checklist',     label: 'Delivery checklist',   icon: ListChecks },
  { key: 'talkingPoints', label: 'Sales talking points', icon: MessageCircle },
  { key: 'objections',    label: 'Objections + answers', icon: MessageCircle },
];

// Read a UK/IN string off either a plain string (legacy) or a {UK,IN} object.
// Legacy fallback = string as-is (used during Feb 2026 pivot rollout window).
const pick = (v, market) => (v && typeof v === 'object' ? (v[market] || v.UK || '') : (v || ''));

export default function AdminDeliveryPlaybooks() {
  const [activeSlug, setActiveSlug] = useState(DELIVERY_PLAYBOOK_ORDER[0]);
  const [market, setMarket] = useState('UK');
  const p = DELIVERY_PLAYBOOKS[activeSlug];

  const copyableMarkdown = useMemo(() => buildMarkdown(p, market), [p, market]);

  function copyMarkdown() {
    try {
      navigator.clipboard.writeText(copyableMarkdown);
      toast.success(`${p.name} playbook copied as markdown`);
    } catch {
      toast.error('Copy failed');
    }
  }

  const pricingLine = typeof p.pricing === 'object'
    ? `${pick(p.pricing, market)}${p.pricing.note ? ' · ' + p.pricing.note : ''}`
    : p.pricing;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6 md:p-10" data-testid="admin-delivery-playbooks">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500 font-mono">
              Client Advisory · {PLAYBOOK_VERSION}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-2 text-white">Delivery Playbooks</h1>
            <p className="text-slate-400 mt-2 max-w-[64ch]">
              How BrightLabs actually delivers each of the six service lines. Same operational depth for UK and India — toggle below.
            </p>
          </div>
          <button
            type="button"
            onClick={copyMarkdown}
            data-testid="admin-playbook-copy-md"
            className="text-[11px] uppercase tracking-[0.22em] font-mono inline-flex items-center gap-2 px-4 py-2.5 rounded-full ring-1 ring-slate-700 text-slate-300 hover:bg-white hover:text-slate-900 hover:ring-white transition"
          >
            Copy as markdown <Copy size={12} />
          </button>
        </div>

        {/* Market + Service switchers — Configurator style */}
        <section className="mt-8 rounded-2xl border border-slate-700 bg-slate-800/50 p-4" data-testid="market-switcher">
          <div className="text-[10px] uppercase tracking-[0.24em] text-slate-400 mb-2 font-mono">Market</div>
          <div className="flex gap-2">
            {['UK', 'IN'].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMarket(m)}
                data-testid={`market-toggle-${m}`}
                className={`text-[11px] uppercase tracking-[0.22em] font-mono px-4 py-2 rounded-full transition ${
                  market === m
                    ? 'bg-white text-slate-900 shadow-lg shadow-white/10'
                    : 'bg-slate-900 text-slate-300 hover:bg-slate-800'
                }`}
              >
                {m === 'UK' ? '🇬🇧 United Kingdom · £' : '🇮🇳 India · ₹'}
              </button>
            ))}
          </div>
        </section>

        <section className="mt-4 rounded-2xl border border-slate-700 bg-slate-800/50 p-4" data-testid="service-switcher">
          <div className="text-[10px] uppercase tracking-[0.24em] text-slate-400 mb-2 font-mono">Service</div>
          <div className="flex flex-wrap gap-2">
            {DELIVERY_PLAYBOOK_ORDER.map((slug) => {
              const s = DELIVERY_PLAYBOOKS[slug];
              const isActive = activeSlug === slug;
              return (
                <button
                  key={slug}
                  type="button"
                  onClick={() => setActiveSlug(slug)}
                  data-testid={`admin-playbook-tab-${slug}`}
                  className={`text-[11px] uppercase tracking-[0.22em] font-mono px-4 py-2.5 rounded-full transition ${
                    isActive
                      ? 'text-white shadow-lg'
                      : 'bg-slate-900 text-slate-300 hover:bg-slate-800'
                  }`}
                  style={isActive ? { background: s.accent, boxShadow: `0 8px 30px -12px ${s.accent}80` } : undefined}
                >
                  {s.name}
                </button>
              );
            })}
          </div>
        </section>

        {/* Service header */}
        <div className="mt-10 border-t border-slate-800 pt-8">
          <div className="text-[11px] uppercase tracking-[0.22em] font-mono" style={{ color: p.accent }}>
            {p.name}
          </div>
          <h2 className="mt-3 text-2xl md:text-3xl font-bold tracking-tight text-white max-w-[36ch]" style={{ fontFamily: 'Fraunces', fontStyle: 'italic', fontWeight: 400 }}>
            {p.tagline}
          </h2>
          <div className="mt-3 text-[13px] font-mono text-slate-400" data-testid="playbook-price-line">{pricingLine}</div>
        </div>

        {/* Sections */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
          <nav className="lg:sticky lg:top-6 lg:self-start">
            <ul className="space-y-1">
              {SECTIONS.filter((s) => {
                if (s.key === 'careAddOns' && !p.careAddOns) return false;
                if (s.key === 'brandingTiers' && !p.brandingTiers) return false;
                return true;
              }).map((s) => (
                <li key={s.key}>
                  <a
                    href={`#pb-${s.key}`}
                    data-testid={`admin-playbook-jump-${s.key}`}
                    className="text-[13px] text-slate-400 hover:text-white transition-colors flex items-center gap-2 py-1.5"
                  >
                    <s.icon size={13} />
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="space-y-12">
            <Section id="pb-pov" icon={Sparkles} title="Our POV" accent={p.accent}>
              <ul className="space-y-4">
                {p.pov.map((item, i) => (
                  <li key={i} className="border-l-2 pl-5 py-1" style={{ borderColor: p.accent }}>
                    <div className="text-[15px] font-semibold text-white" dangerouslySetInnerHTML={{ __html: item.h }} />
                    <p className="mt-1.5 text-[14px] text-slate-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: item.p }} />
                  </li>
                ))}
              </ul>
            </Section>

            <Section id="pb-discovery" icon={HelpCircle} title="Discovery questions" accent={p.accent}>
              <ol className="space-y-3">
                {p.discovery.map((q, i) => (
                  <li key={i} className="grid grid-cols-[28px_1fr] gap-3 items-baseline">
                    <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="text-[14px] text-slate-200 leading-relaxed">
                      <HighlightJargon text={q} />
                    </span>
                  </li>
                ))}
              </ol>
            </Section>

            <Section id="pb-redFlags" icon={AlertTriangle} title="Red flags · when to walk away or redirect" accent={p.accent}>
              <ul className="space-y-3">
                {p.redFlags.map((r, i) => (
                  <li key={i} className="grid grid-cols-[20px_1fr] gap-3 items-start bg-rose-500/10 ring-1 ring-rose-500/25 rounded-md px-4 py-3">
                    <AlertTriangle size={14} className="text-rose-400 mt-0.5" />
                    <span className="text-[14px] text-rose-100 leading-relaxed" dangerouslySetInnerHTML={{ __html: r }} />
                  </li>
                ))}
              </ul>
            </Section>

            <Section id="pb-deliverables" icon={ClipboardList} title="Deliverables · by tier" accent={p.accent}>
              <div className="grid md:grid-cols-3 gap-4">
                {p.deliverables.map((d, i) => (
                  <div key={i} className="rounded-xl bg-slate-800/60 border border-slate-700 p-5 hover:border-slate-500 transition" data-testid={`playbook-tier-${i}`}>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-1" style={{ color: p.accent }}>
                      {pick(d.tier, market)}
                    </div>
                    {d.label && (
                      <div className="text-[12px] text-slate-500 mb-3 italic">{d.label}</div>
                    )}
                    <ul className="space-y-2">
                      {d.items.map((item, j) => (
                        <li key={j} className="text-[13px] text-slate-300 leading-relaxed flex gap-2 items-start">
                          <CheckCircle2 size={12} className="mt-1 shrink-0" style={{ color: p.accent }} />
                          <span dangerouslySetInnerHTML={{ __html: item }} />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </Section>

            {/* NEW: Branding & Identity one-off tiers — shown only under Creative */}
            {p.brandingTiers && (
              <Section id="pb-brandingTiers" icon={Sparkles} title={p.brandingTiers.title} accent={p.accent}>
                <p className="text-[13px] text-slate-400 mb-4 max-w-[70ch]">{p.brandingTiers.note}</p>
                <div className="grid md:grid-cols-3 gap-4" data-testid="playbook-branding-tiers">
                  {p.brandingTiers.tiers.map((t, i) => (
                    <div key={i} className="rounded-xl bg-slate-800/60 border border-slate-700 p-5 hover:border-slate-500 transition" data-testid={`branding-tier-${t.name.toLowerCase()}`}>
                      <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: p.accent }}>{t.name}</div>
                      <div className="mt-2 text-[20px] font-bold text-white">{t[market]}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5 font-mono">{t.turnaround} turnaround</div>
                      <ul className="mt-3 space-y-1.5">
                        {t.items.map((it, j) => (
                          <li key={j} className="text-[12px] text-slate-300 flex gap-2 items-start">
                            <CheckCircle2 size={11} className="mt-1 shrink-0" style={{ color: p.accent }} />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* NEW: Care plan / retainer block — only shown for services that ship one */}
            {p.careAddOns && (
              <Section id="pb-careAddOns" icon={Server} title={p.careAddOns.title} accent={p.accent}>
                <div className="rounded-xl bg-emerald-500/[0.06] border border-emerald-400/25 p-5" data-testid="playbook-care-plans">
                  <div className="grid md:grid-cols-2 gap-3">
                    {p.careAddOns.tiers.map((t, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-slate-900/40 border border-slate-700/70" data-testid={`care-tier-${i}`}>
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-semibold text-white truncate">{t.name}</div>
                          <div className="text-[11px] text-slate-400 mt-0.5">{t.note}</div>
                        </div>
                        <div className="text-[13px] font-mono font-bold whitespace-nowrap" style={{ color: p.accent }}>
                          {t[market]}
                        </div>
                      </div>
                    ))}
                  </div>
                  {p.careAddOns.annualDiscount && (
                    <div className="mt-4 flex items-center gap-2 text-[12px] text-emerald-300">
                      <Sparkles size={12} /> {p.careAddOns.annualDiscount}
                    </div>
                  )}
                </div>
              </Section>
            )}

            <Section id="pb-checklist" icon={ListChecks} title="Delivery checklist" accent={p.accent}>
              <div className="mb-4 flex items-start gap-3 rounded-lg bg-slate-800/60 border border-slate-700 px-4 py-3">
                <Info size={14} className="mt-0.5 shrink-0 text-slate-400" />
                <p className="text-[12px] leading-relaxed text-slate-400">
                  Non-negotiable steps every engagement of this type must go through. Tick as you go.
                  The <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Us</span> pill shows whether brightlabs.live itself does this.
                </p>
              </div>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {p.checklist.map((c, i) => {
                  const audit = SELF_AUDIT[activeSlug]?.[c];
                  return (
                    <li key={i} className="flex items-start gap-3 py-2 border-b border-slate-800">
                      <input type="checkbox" data-testid={`admin-playbook-check-${activeSlug}-${i}`} className="mt-1 accent-white shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-[13px] text-slate-200 leading-relaxed">
                          <HighlightJargon text={c} />
                        </span>
                        {audit && <SelfAuditPill audit={audit} testId={`self-audit-${activeSlug}-${i}`} />}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Section>

            <Section id="pb-talkingPoints" icon={MessageCircle} title="Sales talking points" accent={p.accent}>
              <p className="text-[13px] text-slate-500 mb-4">
                For calls, proposals and email replies. Say it in your own words — these are the beats.
              </p>
              <ul className="space-y-3">
                {p.talkingPoints.map((t, i) => (
                  <li key={i} className="grid grid-cols-[24px_1fr] gap-3 bg-white text-slate-900 rounded-md px-4 py-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 pt-1">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="text-[14px] leading-relaxed">
                      <HighlightJargon text={t} />
                    </span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section id="pb-objections" icon={MessageCircle} title="Objections + honest answers" accent={p.accent}>
              <ul className="space-y-4">
                {p.objections.map((o, i) => (
                  <li key={i} className="rounded-xl bg-slate-800/60 border border-slate-700 p-5">
                    <div className="text-[14px] font-semibold text-white">
                      <span className="font-mono text-[11px] uppercase tracking-[0.22em] mr-2" style={{ color: p.accent }}>Q</span>
                      <HighlightJargon text={o.q} />
                    </div>
                    <div className="mt-3 text-[14px] text-slate-300 leading-relaxed">
                      <span className="font-mono text-[11px] uppercase tracking-[0.22em] mr-2 text-slate-500">A</span>
                      <HighlightJargon text={o.a} />
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-slate-800 text-[11px] font-mono text-slate-500 uppercase tracking-[0.22em]">
          Playbook version {PLAYBOOK_VERSION} · Market: {market === 'UK' ? '🇬🇧 UK' : '🇮🇳 India'} · Edit source at{' '}
          <code className="text-slate-400">/app/frontend/src/data/deliveryPlaybooks.js</code>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function Section({ id, icon: Icon, title, children, accent }) {
  return (
    <section id={id} className="scroll-mt-24">
      <div className="flex items-center gap-2 mb-5 text-white">
        <Icon size={16} style={{ color: accent }} />
        <h3 className="text-lg md:text-xl font-bold tracking-tight">{title}</h3>
      </div>
      {children}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Self-audit pill — shows whether brightlabs.live itself does this           */

const AUDIT_STYLES = {
  yes:     { label: 'Us · Yes',     dot: 'bg-emerald-500', ring: 'ring-emerald-500/40', bg: 'bg-emerald-500/10', text: 'text-emerald-300' },
  partial: { label: 'Us · Partial', dot: 'bg-amber-500',   ring: 'ring-amber-500/40',   bg: 'bg-amber-500/10',   text: 'text-amber-300' },
  no:      { label: 'Us · No',      dot: 'bg-rose-500',    ring: 'ring-rose-500/40',    bg: 'bg-rose-500/10',    text: 'text-rose-300' },
  na:      { label: 'Us · N/A',     dot: 'bg-slate-500',   ring: 'ring-slate-600',      bg: 'bg-slate-800',      text: 'text-slate-400' },
};

function SelfAuditPill({ audit, testId }) {
  const style = AUDIT_STYLES[audit.status] || AUDIT_STYLES.na;
  return (
    <Popover>
      <PopoverTrigger asChild data-testid={testId}>
        <button
          type="button"
          className={`mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full ring-1 font-mono text-[10px] uppercase tracking-[0.18em] ${style.bg} ${style.ring} ${style.text} hover:brightness-110 transition`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
          {style.label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-[340px] bg-slate-900 text-slate-100 ring-1 ring-slate-700 shadow-2xl rounded-lg p-4"
      >
        <div className="flex items-center gap-2 mb-2">
          <span className={`w-2 h-2 rounded-full ${style.dot}`} />
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
            brightlabs.live · {audit.status}
          </span>
        </div>
        <p className="text-[13px] leading-relaxed text-slate-200">{audit.note}</p>
      </PopoverContent>
    </Popover>
  );
}

/* -------------------------------------------------------------------------- */

function stripHtml(s) {
  return String(s || '').replace(/&amp;/g, '&').replace(/&mdash;/g, '—').replace(/&ldquo;/g, '"').replace(/&rdquo;/g, '"').replace(/&rsquo;/g, "'").replace(/<[^>]+>/g, '');
}

function buildMarkdown(p, market) {
  const lines = [];
  const priceStr = typeof p.pricing === 'object' ? `${pick(p.pricing, market)}${p.pricing.note ? ' · ' + p.pricing.note : ''}` : p.pricing;
  lines.push(`# ${p.name} — Delivery Playbook ${PLAYBOOK_VERSION}`);
  lines.push('');
  lines.push(`> ${stripHtml(p.tagline)}`);
  lines.push('');
  lines.push(`**Pricing (${market}):** ${priceStr}`);
  lines.push('');

  lines.push('## Our POV');
  p.pov.forEach((i) => lines.push(`- **${stripHtml(i.h)}** ${stripHtml(i.p)}`));
  lines.push('');

  lines.push('## Discovery Questions');
  p.discovery.forEach((q, i) => lines.push(`${i + 1}. ${q}`));
  lines.push('');

  lines.push('## Red Flags');
  p.redFlags.forEach((r) => lines.push(`- ${stripHtml(r)}`));
  lines.push('');

  lines.push('## Deliverables');
  p.deliverables.forEach((d) => {
    lines.push(`### ${pick(d.tier, market)}${d.label ? ` · ${d.label}` : ''}`);
    d.items.forEach((it) => lines.push(`- ${stripHtml(it)}`));
    lines.push('');
  });

  if (p.brandingTiers) {
    lines.push(`## ${p.brandingTiers.title}`);
    lines.push(`_${p.brandingTiers.note}_`);
    lines.push('');
    p.brandingTiers.tiers.forEach((t) => {
      lines.push(`### ${t.name} — ${t[market]} · ${t.turnaround}`);
      t.items.forEach((it) => lines.push(`- ${it}`));
      lines.push('');
    });
  }

  if (p.careAddOns) {
    lines.push(`## ${p.careAddOns.title}`);
    p.careAddOns.tiers.forEach((t) => {
      lines.push(`- **${t.name}** — ${t[market]} · ${t.note}`);
    });
    if (p.careAddOns.annualDiscount) lines.push(`\n_${p.careAddOns.annualDiscount}_`);
    lines.push('');
  }

  lines.push('## Delivery Checklist');
  p.checklist.forEach((c) => lines.push(`- [ ] ${c}`));
  lines.push('');

  lines.push('## Sales Talking Points');
  p.talkingPoints.forEach((t) => lines.push(`- ${t}`));
  lines.push('');

  lines.push('## Objections + Answers');
  p.objections.forEach((o) => {
    lines.push(`**Q:** ${o.q}`);
    lines.push('');
    lines.push(`**A:** ${stripHtml(o.a)}`);
    lines.push('');
  });

  return lines.join('\n');
}
